import React, { useState, useCallback } from 'react';
import { dataGridSx } from '../../common/styled/DataGrid';
import pallet from '../../../layouts/pallet';
import { DataGrid } from '@mui/x-data-grid';
import {
  AcademicYearHeaderWrapper,
  AcademicYearInfoActionWrapper,
  DeleteClassButton,
  SectionTitleText
} from '../styled';
import { StyledButton } from '../../Exams/styled';
import Icons from '../../../components/icons';
import { getStaffAcademicTableColumns } from '../utils/staffAcademicTableColumn';
import { ClassesToBeDeletedProps, ClassInfo, TeacherProfile } from '../types';
import { SnackbarSeverityType, UserRole } from '../../../enums';
import userApi from '../../../apis/users';
import timeUtils from '../../../utils/timeUtils';
import tableColumnUtils from '../../../utils/tableColumnUtils';
import CustomDataGridToolbar from '../../common/components/CustomDataGridToolbar';

const getRowIdString = (row: ClassesToBeDeletedProps) => {
  return encodeURIComponent(
    [row.grade ?? '', row.division ?? '', row.section ?? ''].join('|')
  );
};

const AcademicYearClassTable = ({
  staffDetailData,
  handleOpenAddClassModal,
  handleOpenEditClassModal,
  showToast,
  onDeleteClassSuccess,
  isLoading
}: {
  staffDetailData: TeacherProfile | undefined;
  handleOpenAddClassModal: () => void;
  handleOpenEditClassModal: (Id: string) => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onDeleteClassSuccess: () => void;
  isLoading: boolean;
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const academicYear = staffDetailData?.currentCourseInfo?.academicYear;

  const classes =
    staffDetailData?.currentCourseInfo?.classes.map((classInfo) => ({
      ...classInfo,
      academicYear: timeUtils.getYearRange(
        academicYear?.from || '',
        academicYear?.to || ''
      )
    })) ?? [];
  const allRowIds = classes.map(getRowIdString);

  const allRowsSelected =
    selectedRows.length === allRowIds.length && allRowIds.length > 0;
  const isIndeterminate =
    selectedRows.length > 0 && selectedRows.length < allRowIds.length;

  const onHeaderCheckboxClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedRows(allRowIds);
      } else {
        setSelectedRows([]);
      }
    },
    [allRowIds]
  );

  const onCellCheckboxClick = useCallback((id: string) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(id)) {
        return prevSelectedRows.filter((rowId) => rowId !== id);
      } else {
        return [...prevSelectedRows, id];
      }
    });
  }, []);

  const getClassesToBeDeletedRequest = (selectedClassData: ClassInfo[]) => {
    const classesToBeDeletedRequest: ClassesToBeDeletedProps[] = [];

    selectedClassData.forEach((selectedRow) => {
      if (selectedRow.grade && selectedRow.division) {
        classesToBeDeletedRequest.push({
          grade: selectedRow.grade,
          division: selectedRow.division
        });
      } else if (selectedRow.section) {
        classesToBeDeletedRequest.push({ section: selectedRow.section });
      }
    });

    return classesToBeDeletedRequest;
  };

  const handleDeleteClass = async () => {
    if (!staffDetailData?.id) return;
    setIsDeleting(true);

    const selectedClassData =
      classes.filter((classItem) =>
        selectedRows.includes(getRowIdString(classItem))
      ) || [];

    const classesToBeDeletedRequest =
      getClassesToBeDeletedRequest(selectedClassData);

    const academicYearRange =
      staffDetailData?.currentAcademicYear ||
      timeUtils.getYearRange(
        staffDetailData?.currentCourseInfo?.academicYear?.from || '',
        staffDetailData?.currentCourseInfo?.academicYear?.to || ''
      );

    const payload = {
      event: 'DELETE_ACADEMIC_YEAR_WISE_MAPPINGS',
      academicYear: academicYearRange,
      classesToBeDeleted: classesToBeDeletedRequest,
      grade: '-',
      division: '-',
      subjects: [],
      isClassTeacher: false,
      activityClubs: staffDetailData?.currentCourseInfo?.activityClubs || []
    };
    try {
      await userApi.updateCourseInfo(staffDetailData?.id, payload);
      onDeleteClassSuccess();
      setSelectedRows([]);
      showToast('Class deleted successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message || 'Error deleting class',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const academicYearColumns = getStaffAcademicTableColumns(
    handleOpenEditClassModal,
    onHeaderCheckboxClick,
    onCellCheckboxClick,
    selectedRows,
    allRowsSelected,
    isIndeterminate,
    isDeleting
  );

  return (
    <>
      <AcademicYearHeaderWrapper>
        <SectionTitleText>Academic Year Information</SectionTitleText>
        <AcademicYearInfoActionWrapper>
          <StyledButton
            style={{ borderRadius: 8 }}
            onClick={handleOpenAddClassModal}
          >
            <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add New Class
          </StyledButton>
          <DeleteClassButton
            variant='contained'
            disabled={selectedRows.length === 0}
            onClick={handleDeleteClass}
          >
            <Icons.TrashWhite />
          </DeleteClassButton>
        </AcademicYearInfoActionWrapper>
      </AcademicYearHeaderWrapper>
      <DataGrid
        rowHeight={50}
        rows={classes}
        columns={academicYearColumns}
        loading={isLoading}
        disableRowSelectionOnClick
        pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
        initialState={tableColumnUtils.DATA_GRID_INITIAL_STATE}
        columnVisibilityModel={{
          action: staffDetailData?.role === UserRole.TEACHER ? true : false
        }}
        getRowId={getRowIdString}
        showToolbar
        slots={{ toolbar: CustomDataGridToolbar }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true
          }
        }}
        sx={{
          ...dataGridSx(pallet),
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto'
          }
        }}
      />
    </>
  );
};

export default AcademicYearClassTable;
